import React from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useGetCollectionsNavigationQuery } from 'types/graphql';
import ROUTES from 'src/routes';
import usePaths from 'src/hooks/use-paths';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useDispensaryCategories from 'src/dispensary/hooks/use-dispensary-categories';
import { tracker } from 'src/utils/analytics';
import { LoginCta } from 'src/components/ctas';
import { ConditionalLink } from 'src/utils/conditional-link';
import { getCustomCategoryLink } from 'src/dispensary/hooks/use-get-custom-category-link';
import { useActiveScheduledItems } from 'src/dispensary/hooks/use-active-scheduled-items';

type ShopTabProps = {
  onClose: () => void;
};

export const ShopTab = ({ onClose }: ShopTabProps): JSX.Element => {
  const flags = useFlags();
  const isNextLinkEnabled = flags['ecomm.use-next-link-in-shop-tab.rollout'] ?? false;
  const { dispensary } = useDispensary();
  const { categories } = useDispensaryCategories();
  const { href: baseHref } = usePaths();
  const { COLLECTIONS } = ROUTES;

  const handleLinkClick = (label: string): void => {
    tracker.gaGTMClickEvent({
      eventCategory: `navigation click`,
      eventAction: `main menu`,
      eventLabel: label,
    });

    onClose();
  };

  const { data } = useGetCollectionsNavigationQuery({
    skip: !dispensary?.id,
    variables: {
      dispensaryId: dispensary.id,
      customCategory: false,
      enabled: true,
      activeOnly: true,
    },
  });

  const collections = useActiveScheduledItems({
    items: data?.getCollectionsNavigation ?? [],
    getSchedule: (collection) => collection.schedule,
    dispensaryTimezone: dispensary?.timezone,
    enablePolling: true,
  });

  return (
    <Container>
      <Nav>
        <div>
          <NavHeading>Categories</NavHeading>
          <NavList>
            {categories.map(({ key, label, slug, customCategory }) => {
              const href = getCustomCategoryLink(baseHref, customCategory, slug, key);
              if (!href) {
                return null;
              }
              if (isNextLinkEnabled) {
                return (
                  <li key={key}>
                    <NavLink
                      href={href}
                      onClick={() => handleLinkClick(`categories > ${String(label)}`)}
                      data-testid='menu-drawer-category-link'
                    >
                      {label}
                    </NavLink>
                  </li>
                );
              }
              return (
                <li key={key}>
                  <ConditionalLink url={href}>
                    <LegacyNavLink
                      href={href}
                      onClick={() => handleLinkClick(`categories > ${String(label)}`)}
                      data-testid='menu-drawer-category-link'
                    >
                      {label}
                    </LegacyNavLink>
                  </ConditionalLink>
                </li>
              );
            })}
          </NavList>
        </div>

        {collections.length > 0 && (
          <div>
            <NavHeading>Collections</NavHeading>
            <NavList>
              {collections.map(({ slug, navigationTitle, title }) => (
                <li key={slug}>
                  {isNextLinkEnabled ? (
                    <NavLink
                      href={`${baseHref}${COLLECTIONS}${slug}`}
                      onClick={() => handleLinkClick(`collections > ${navigationTitle ?? title}`)}
                    >
                      {navigationTitle ?? title}
                    </NavLink>
                  ) : (
                    <ConditionalLink url={`${baseHref}${COLLECTIONS}${slug}`}>
                      <LegacyNavLink
                        href={`${baseHref}${COLLECTIONS}${slug}`}
                        onClick={() => handleLinkClick(`collections > ${navigationTitle ?? title}`)}
                      >
                        {navigationTitle ?? title}
                      </LegacyNavLink>
                    </ConditionalLink>
                  )}
                </li>
              ))}
            </NavList>
          </div>
        )}
      </Nav>

      <LoginCta />
    </Container>
  );
};

const Container = styled.div`
  padding: var(--dcm-drawer-nav-padding);
  max-height: 100%;
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: var(--dcm-drawer-nav-gap);
  margin-bottom: var(--dcm-drawer-nav-gap);
`;

const NavHeading = styled.h2`
  color: var(--dcm-drawer-nav-heading-color);
  font-size: var(--dcm-drawer-nav-heading-font-size);
  font-weight: var(--dcm-drawer-nav-heading-font-weight);
  line-height: 1;
  margin: var(--dcm-drawer-nav-gap) 0;
`;

const NavList = styled.ul`
  font-size: var(--dcm-drawer-nav-link-font-size);
  list-style: none;
  margin: 0;
  padding: 0;
`;

const NavLink = styled(Link)`
  color: var(--dcm-drawer-nav-link-color);
  display: block;
  font-size: var(--dcm-drawer-nav-link-font-size);
  line-height: 1;
  padding: var(--dcm-drawer-nav-link-padding);
`;

const LegacyNavLink = styled.a`
  color: var(--dcm-drawer-nav-link-color);
  display: block;
  font-size: var(--dcm-drawer-nav-link-font-size);
  line-height: 1;
  padding: var(--dcm-drawer-nav-link-padding);
`;
